import { useLocation } from "wouter";
import { ArrowRight, Sparkles, Cpu, Network, FileCheck2, ShieldCheck } from "lucide-react";
import { decomporHonorarios, brl } from "../../../shared/rateio";
import { useFaixasRateio } from "@/lib/faixas";  // tabela viva (config do admin)
import { useT } from "@/lib/i18n";  // PR #162: wiring i18n na home

// ─── Logo ACORDEI! ────────────────────────────────────────────────────────────

const LOGO_URL = "/acordei-logo-v2.png";




// ─── Página Principal ─────────────────────────────────────────────────────────
export default function Home() {
  const [, navigate] = useLocation();
  const t = useT();  // PR #162: hook i18n — re-render automatico ao trocar idioma
  const faixasRateio = useFaixasRateio();  // tabela viva (reflete config do admin; fallback estatico)

  // Azul marinho: usa os tokens do CSS
  const navyBg = "bg-background";
  const navySection = "bg-muted";

  return (
    <div className={`min-h-screen ${navyBg} mesh-bg`}>

      {/* ── Navbar ── */}
      <header className={`sticky top-0 z-50 ${navyBg} border-b border-white/8 backdrop-blur-xl`}>
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo + subtítulo EMPRESAS (discreto, sob o wordmark) */}
            <div className="flex items-center gap-2.5">
              <img src={LOGO_URL} alt="ACORDEI!" className="w-8 h-8 object-contain" />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-xl font-bold text-white tracking-tight">
                  ACORDEI<span className="text-cyan-400">!</span>
                </span>
                <span className="text-[9px] font-semibold tracking-[0.32em] text-white/35 uppercase mt-0.5">
                  Empresas
                </span>
              </div>
            </div>

            <nav aria-label="Navegação principal" className="hidden md:flex items-center gap-4 text-sm text-white/60 whitespace-nowrap">
              {/* PR #140: itens "como funciona" e "setores" só em lg+ pra evitar overflow */}
              <button onClick={() => navigate("/servicos")} className="hidden lg:inline hover:text-white transition-colors">Serviços</button>
              <button onClick={() => navigate("/tecnologia")} className="hidden lg:inline hover:text-white transition-colors">Tecnologia</button>
              <a href="#honorarios" className="hidden lg:inline hover:text-white transition-colors">Honorários</a>
              <button onClick={() => navigate("/mapa")} className="hidden xl:inline hover:text-cyan-400 transition-colors">
                {t("home.nav_mapa_fluxo")}
              </button>
              {/* "Acompanhar Protocolo" removido do menu — o acesso agora é pela
                  área logada (botão Entrar na barra global). */}
              <button onClick={() => navigate("/faq")} className="hidden xl:inline hover:text-cyan-400 transition-colors">
                {t("home.nav_faq")}
              </button>
              {/* Mediador/Admin e o cross-link ao Consumidor vivem só na barra
                  global (HeaderGlobal) — removidos daqui pra não duplicar o
                  cabeçalho. O site Empresas não linka "Sou consumidor". */}
            </nav>

            <button
              onClick={() => navigate("/servicos")}
              aria-label={t("home.cta_iniciar_consulta")}
              className="btn-press inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[oklch(0.11_0.04_255)] text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
            >
              {t("home.cta_iniciar_consulta")}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main id="conteudo-principal">
      {/* ── Hero ── */}
      <section aria-labelledby="hero-titulo" className={`relative overflow-hidden ${navyBg} pt-24 pb-32`}>
        {/* Decorações de fundo */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Glow ciano superior direito */}
          <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-[120px]" />
          {/* Glow violeta inferior esquerdo */}
          <div className="absolute bottom-0 -left-32 w-[500px] h-[500px] rounded-full bg-violet-600/12 blur-[100px]" />
        </div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge — "Recuperação tributária administrativa" em destaque maior */}
            <div className="inline-flex items-center gap-2.5 bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 text-base md:text-lg font-semibold px-6 py-2.5 rounded-full mb-8 animate-fade-up">
              <Sparkles className="w-5 h-5" />
              {t("home.badge_mediacao")}
            </div>

            {/* Logo grande + subtítulo EMPRESAS */}
            <div className="flex flex-col items-center mb-6 animate-fade-up stagger-1">
              <img src={LOGO_URL} alt="ACORDEI!" className="w-20 h-20 object-contain animate-float" />
              <span className="text-[10px] font-semibold tracking-[0.35em] text-white/35 uppercase mt-2">
                Empresas
              </span>
            </div>

            {/* Chamada de atenção ao contador — cor própria (âmbar), caixa alta,
                em parágrafo separado acima do título. */}
            <p className="font-serif text-2xl md:text-3xl font-extrabold text-amber-400 uppercase tracking-tight mb-6 animate-fade-up stagger-1">
              Atenção, contador!
            </p>

            {/* Título */}
            <h1 id="hero-titulo" className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-6 animate-fade-up stagger-2">
              {t("home.hero_titulo_l1")}<br />
              <span className="gradient-brand-text">{t("home.hero_titulo_l2")}</span>
            </h1>

            {/* Subtítulo — o valor PRO CONTADOR em DESTAQUE */}
            <p className="text-lg md:text-xl text-white/55 leading-relaxed mb-4 max-w-2xl mx-auto animate-fade-up stagger-3">
              A ACORDEI! roda na{" "}
              <strong className="text-cyan-300 font-semibold">carteira inteira de CNPJs</strong> do
              seu escritório e descobre quais clientes têm{" "}
              <strong className="text-white font-semibold">crédito tributário recuperável</strong> —
              PIS/COFINS monofásico, INSS sobre verbas, Tema 69 e mais. Cerca de até{" "}
              <strong className="text-white font-semibold">360h de homens-hora</strong> da sua equipe
              trabalhando por meses, por CNPJ, viram apenas{" "}
              <strong className="text-emerald-300 font-semibold">15 minutos de revisão</strong>.
            </p>
            <p className="text-xl md:text-2xl font-serif font-bold text-emerald-300 mb-3 animate-fade-up stagger-3">
              Sem mensalidade. A empresa cliente só paga se recuperar.
            </p>
            {/* Narrativa de PARCERIA (texto canônico do Arthur): uma única
                comissão de êxito por faixa (success fee), rateada entre o
                honorário do contador e a tecnologia da plataforma. Ninguém
                paga adiantado — só quando o crédito é efetivamente recuperado. */}
            <p className="text-sm md:text-base text-white/55 mb-10 max-w-2xl mx-auto animate-fade-up stagger-3">
              Você entra em parceria com a <strong className="text-white/85">ACORDEI!</strong>. Há uma
              única comissão de êxito por faixa (<em className="text-white/70">success fee</em>),
              rateada entre seus honorários como contador e a tecnologia de ponta da plataforma,
              baseada em inteligência artificial, <em className="text-white/70">machine learning</em>,
              inferência neural e modelagem quantitativa estatística.{" "}
              <strong className="text-emerald-300">Ninguém paga nada antecipadamente: a comissão só é
              devida pela empresa para ambos quando o crédito for efetivamente recuperado!</strong>
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up stagger-4">
              <button
                onClick={() => navigate("/servicos")}
                className="btn-press inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[oklch(0.11_0.04_255)] font-bold px-8 py-3.5 rounded-xl shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all"
              >
                {t("home.cta_comecar")}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/mapa")}
                className="inline-flex items-center gap-2 text-white/70 font-medium px-6 py-3.5 rounded-xl border border-white/15 hover:bg-white/8 hover:text-white transition-all"
              >
                {t("home.como_funciona")}
              </button>
            </div>

            {/* Acessos Mediador/Admin ficam SÓ no cabeçalho (nav) — não no
                corpo da página. Removido daqui a pedido do Arthur. */}
          </div>

        </div>
      </section>

      {/* ── Tecnologia proprietária (pedido do Arthur): integração oficial com a
           Receita + automação por IA = o que o escritório não monta sozinho.
           HONESTO: descreve a CAPACIDADE da plataforma (motor IA já vivo + arquitetura
           de integração oficial SERPRO), sem afirmar transmissão 100% automática. ── */}
      <section aria-labelledby="tec-titulo" className={`relative overflow-hidden py-20 ${navySection} border-t border-white/8`}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-1/3 w-[480px] h-[480px] rounded-full bg-violet-600/10 blur-[120px]" />
        </div>
        <div className="container relative">
          <div className="text-center mb-14 animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-violet-300 uppercase mb-3">
              <Sparkles className="w-4 h-4" /> Tecnologia proprietária
            </span>
            <h2 id="tec-titulo" className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
              Uma infraestrutura que o escritório <span className="gradient-brand-text">não monta sozinho</span>
            </h2>
            <p className="text-white/55 max-w-3xl mx-auto leading-relaxed">
              O ACORDEI! une um <strong className="text-white/85">motor de auditoria por inteligência
              artificial</strong> à <strong className="text-white/85">integração com os sistemas oficiais
              da Receita Federal</strong>. É engenharia de software — não um robô de tela, nem planilha —
              operando no <strong className="text-cyan-300">canal legítimo</strong> da Receita. Tecnologia
              que exige tempo, contrato e equipe pra existir: a gente já montou, você usa no seu painel.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Pilar 1 — Motor IA (já vivo) */}
            <div className="rounded-2xl border border-white/10 bg-card p-6 animate-fade-up">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/25 mb-4">
                <Cpu className="w-5 h-5 text-cyan-300" />
              </div>
              <h3 className="font-serif text-lg font-bold text-white mb-2">Motor de auditoria por IA</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                Varre a carteira inteira de CNPJs, classifica NCM e teses com IA (rede neural + regras +
                revisão humana) e calcula o indébito de 5 anos com memória de cálculo e linhagem
                rastreável. Escala que nenhuma equipe faz no braço.
              </p>
            </div>
            {/* Pilar 2 — Integração oficial Receita */}
            <div className="rounded-2xl border border-white/10 bg-card p-6 animate-fade-up stagger-1">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-violet-500/10 border border-violet-500/25 mb-4">
                <Network className="w-5 h-5 text-violet-300" />
              </div>
              <h3 className="font-serif text-lg font-bold text-white mb-2">Integração oficial com a Receita</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                Conexão com as <strong className="text-white/80">APIs oficiais da Receita Federal</strong>{" "}
                (SERPRO Integra Contador) pra conferir dados fiscais e preparar os pedidos no canal
                autorizado. Exige contrato, certificado e infraestrutura que um escritório isolado não tem.
              </p>
            </div>
            {/* Pilar 3 — Submissão guiada */}
            <div className="rounded-2xl border border-white/10 bg-card p-6 animate-fade-up stagger-2">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 mb-4">
                <FileCheck2 className="w-5 h-5 text-emerald-300" />
              </div>
              <h3 className="font-serif text-lg font-bold text-white mb-2">Submissão guiada no e-CAC</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                A plataforma entrega o <strong className="text-white/80">roteiro pronto</strong> de
                retificação e PER/DCOMP por competência — campos, base de cálculo e o passo a passo no
                e-CAC. Você transmite em minutos.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-10 text-xs text-white/40 animate-fade-up">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Canal oficial da Receita — nada de robô de tela (RPA). Conformidade e segurança por construção.
          </div>
        </div>
      </section>

      {/* ── Honorário de êxito / rateio (parceria transparente) ── */}
      <section id="honorarios" className={`py-20 ${navyBg}`}>
        <div className="container">
          <div className="text-center mb-12 animate-fade-up">
            <span className="inline-block text-xs font-semibold tracking-widest text-emerald-400 uppercase mb-3">
              Honorário de êxito
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
              Uma parceria. Um único honorário.
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Sem mensalidade e sem risco: há um <strong className="text-white/75">único honorário de êxito</strong>,
              regressivo por faixa, cobrado só sobre o que for <strong className="text-white/75">efetivamente
              recuperado</strong>. Ele é repartido, de forma transparente, entre a tecnologia da
              ACORDEI! (fee) e o trabalho técnico do contador parceiro.
            </p>
          </div>

          {/* Tabela de faixas (fonte: shared/rateio.ts) */}
          <div className="max-w-3xl mx-auto overflow-x-auto rounded-2xl border border-white/10 bg-card animate-fade-up">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-white/45 border-b border-white/10">
                  <th className="py-3.5 px-4">Volume recuperado</th>
                  <th className="py-3.5 px-4 text-right">Honorário total</th>
                  <th className="py-3.5 px-4 text-right">Fee plataforma</th>
                  <th className="py-3.5 px-4 text-right">Honorário do contador</th>
                </tr>
              </thead>
              <tbody>
                {faixasRateio.map((f) => (
                  <tr key={f.indice} className="border-b border-white/6 last:border-0">
                    <td className="py-3.5 px-4 text-white/80">{f.rotulo}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-emerald-300">{(f.pctTotal * 100).toFixed(0)}%</td>
                    <td className="py-3.5 px-4 text-right text-cyan-300">{(f.pctFee * 100).toFixed(0)}%</td>
                    <td className="py-3.5 px-4 text-right text-violet-300">{(f.pctHonorario * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Exemplo de decomposição (mesma função usada no cálculo) */}
          {(() => {
            const ex = decomporHonorarios(60_000);
            return (
              <div className="max-w-2xl mx-auto mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 animate-fade-up">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-300/80 mb-4">
                  Exemplo — crédito de {brl(ex.creditoAproveitado)} recuperado (faixa 1)
                </p>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-white/60">Crédito recuperado</dt><dd className="text-white font-medium">{brl(ex.creditoAproveitado)}</dd></div>
                  <div className="flex justify-between"><dt className="text-white/60">(−) Honorário total ({(ex.faixa.pctTotal * 100).toFixed(0)}%)</dt><dd className="text-white/80">{brl(ex.totalHonorarios)}</dd></div>
                  <div className="flex justify-between pl-5"><dt className="text-cyan-300/80">├─ Fee da plataforma ({(ex.faixa.pctFee * 100).toFixed(0)}%)</dt><dd className="text-cyan-300">{brl(ex.feePlataforma)}</dd></div>
                  <div className="flex justify-between pl-5"><dt className="text-violet-300/80">└─ Honorário do contador ({(ex.faixa.pctHonorario * 100).toFixed(0)}%)</dt><dd className="text-violet-300">{brl(ex.honorarioContador)}</dd></div>
                  <div className="flex justify-between border-t border-white/10 pt-2 mt-1"><dt className="text-emerald-300 font-semibold">(=) Líquido ao cliente</dt><dd className="text-emerald-300 font-bold">{brl(ex.liquidoCliente)}</dd></div>
                </dl>
              </div>
            );
          })()}

          <p className="text-center text-xs text-white/35 mt-6 max-w-2xl mx-auto">
            Base de cálculo: valor efetivamente aproveitado (compensado/restituído), nunca apenas
            homologado. A faixa é definida pelo volume recuperado.
          </p>
        </div>
      </section>

      {/* ── CTA Final ── */}
      <section className={`py-20 ${navySection} border-t border-white/8`}>
        <div className="container text-center">
          {/* Glow decorativo */}
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-cyan-500/20 blur-3xl rounded-full scale-150" />
            <img src={LOGO_URL} alt="ACORDEI!" loading="lazy" className="relative w-16 h-16 object-contain animate-float" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4 animate-fade-up">
            Pronto para recuperar o que sua empresa pagou a mais?
          </h2>
          <p className="text-white/50 mb-8 max-w-lg mx-auto animate-fade-up stagger-1">
            Faça o diagnóstico agora. É rápido, gratuito e sem compromisso.
          </p>
          <button
            onClick={() => navigate("/servicos")}
            className="btn-press inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[oklch(0.11_0.04_255)] font-bold px-8 py-3.5 rounded-xl shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_50px_rgba(6,182,212,0.5)] transition-all animate-fade-up stagger-2"
          >
            Iniciar consulta gratuita
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      </main>

      {/* ── Footer ── */}
      <footer className={`${navyBg} border-t border-white/8 py-10`}>
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img src={LOGO_URL} alt="ACORDEI!" loading="lazy" className="w-6 h-6 object-contain opacity-80" />
              <span className="font-serif text-lg font-bold text-white">
                ACORDEI<span className="text-cyan-400">!</span>
              </span>
            </div>
            <p className="text-xs text-white/30 text-center">
              © {new Date().getFullYear()} ACORDEI! — Plataforma de Recuperação Tributária
              Administrativa. Todos os direitos reservados.
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-white/40">
              <a href="/blog" className="hover:text-white transition-colors">Blog</a>
              <a href="/impacto" className="hover:text-white transition-colors">Impacto</a>
              <a href="/faq" className="hover:text-white transition-colors">FAQ</a>
              <a href="/privacidade" className="hover:text-white transition-colors">Privacidade</a>
              <a href="/termos" className="hover:text-white transition-colors">Termos de Uso</a>
              <a href="/contrato" className="hover:text-white transition-colors">Contrato</a>
              <a href="mailto:suporte@acordei.app.br" className="hover:text-white transition-colors">Contato</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
